import ReactGA from 'react-ga4'
import Cookies from 'universal-cookie'
const cookies = new Cookies()
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

// https://javascript.plainenglish.io/the-ultimate-guide-to-google-analytics-ua-ga4-on-react-or-anything-else-ec9dc84c0211

/**
 *  If you change or create new google analytics account, you have to:
 *  create each event we want to track on google analytics admin interface manually (otherwise parameter we pass to event will
 *  not be tracked by google), also page_view! and
 *
 *  configure analytics so that page views on history change is not tracked by google! Therefore uncheck
 *  "Page changes based on browser history events" in advanced settings. Otherwise we track a page twice.
 *
 *  Data may need 48hours to be shown in analytics and only after event was created in google analytics admin interface.
 */
const debugMode = true
const debugLog = false

export type TrackCategory = 'audio' | 'pageInteraction' | 'info'
export type TrackAudioAction =
  'playClick'
  | 'pauseClick'
  | 'rewind15secClick'
  | 'forward15secClick'
  | 'seekClick'
  | 'audioEnded'
  | 'previousClick'
  | 'nextClick'
  | 'lastPlayPositionOnStationExit'
  | 'sumPlayedOnStationExit'
  | 'sumPlayedOnStationExitFlooredToFifth'
export type TrackPageInteractionAction = 'stationLinkInListClick' | 'showOverviewClick' | 'hideOverviewClick' | 'frontPageOkButtonClick' | 'previousStationLinkClick' | 'nextStationLinkClick'
export type TrackInfoAction = 'usedLanguage'

// open in firefox ;) https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference?hl=de
export const initGA = () => { // only if is isUserExperienceCookiesAllowed
  if (debugMode || process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'development') {
    if (debugLog) {
      console.log('%ctrack: initialize GA with: ' + process.env.GATAG, 'color: grey;')
    }
    ReactGA.initialize([{
      trackingId: process.env.GATAG,
      gaOptions: {
        debug_mode: debugMode,
        // cookieDomain: debugMode ? 'stephansdom-audioguide-dev.illustree.at' : '',
        // anonymizeIp: true, // if we anonymize we dont get realtime info
        // allowAdFeatures: false, // infos like age / gender / interests
        send_page_view: false,
        sendPageView: false
      },
      gtagOptions: {
        send_page_view: false, // we don't use automatic page view tracking of GoogleAnalytics4! We implement it on our own. equals: gtag('config', 'G-xxxxxxxxxx', { send_page_view: false })
        sendPageView: false,
        debug_mode: debugMode
      }
    }
    ])
  }
}

export const trackLanguage = (isUserExperienceCookiesAllowed, selectedLanguage) => {
  if (debugLog) {
    console.log('%ctrack info: (tracking allowed: ' + isUserExperienceCookiesAllowed + ')', 'color: grey;')
    console.log('    action: usedLanguage')
    console.log('     label: ', selectedLanguage)

  }
  if (isUserExperienceCookiesAllowed) {
    ReactGA.event({
      category: 'info',
      action: ('usedLanguage' as TrackInfoAction),
      label: selectedLanguage, // optional
      nonInteraction: true, // optional, true/false
      transport: 'xhr' // optional, beacon/xhr/image
    })
  }
}

export const usePageTracking = (isUserExperienceCookiesAllowed, title, selectedLanguage) => {
  const location = useLocation()

  useEffect(() => {
    if (debugLog) {
      console.log('%ctrack page: (tracking allowed: ' + isUserExperienceCookiesAllowed + ')', 'color: grey;')
      console.log('      path: ', location.pathname + location.search + location.hash)
      console.log('     title: ', title)
      console.log('    search: ', location.search)
    }
    if (isUserExperienceCookiesAllowed) {
      ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search + location.hash, title: title })
    }

    // window.gtag('event', 'page_view', {
    //   page_path: location.pathname + location.search + location.hash,
    //   page_search: location.search,
    //   page_hash: location.hash
    // })
  }, [location]) // whenever url changes
}

export const trackAudio = (isUserExperienceCookiesAllowed, action: TrackAudioAction, srcOrId, isNonInteraction, value?) => {
  let id = srcOrId

  if (isNaN(id)) {
    id = id.slice(id.lastIndexOf('/') + 1)
    id = parseInt(id.slice(0, id.indexOf('-')))
  }

  if (debugLog) {
    console.log('%ctrack audio: (tracking allowed: ' + isUserExperienceCookiesAllowed + ')', 'color: grey;')
    console.log('     action: ', action)
    console.log('      label: ', 'stationId ' + id)
    console.log('      value: ', value)
  }
  if (isUserExperienceCookiesAllowed)
    ReactGA.event({
      category: 'audio',
      action: action,
      label: 'stationId: ' + id, // optional
      value: !isNaN(value) ? value : undefined, // optional, must be a number
      nonInteraction: isNonInteraction, // optional, true/false
      transport: 'xhr' // optional, beacon/xhr/image
    })
}

export const trackPageInteraction = (isUserExperienceCookiesAllowed, action: TrackPageInteractionAction, label?, value?) => {
  if (debugLog) {
    console.log('%ctrackPageInteraction: (tracking allowed: ' + isUserExperienceCookiesAllowed + ')', 'color: grey;')
    console.log('              action: ', action)
    console.log('               label: ', label)
    console.log('               value: ', value)
  }

  ReactGA.event({ //
    category: 'trackPageInteraction',
    action: action,
    label: label ? label : undefined, // optional
    value: !isNaN(value) ? value : undefined, // optional, must be a number
    nonInteraction: false, // optional, true/false
    transport: 'xhr' // optional, beacon/xhr/image
  })
}

//
export const removeGA = () => {
  if (debugLog) {
    console.log('%cremove tracking', 'color: grey;')
  }
  cookies.remove('_ga')
  cookies.remove('_gat')
  cookies.remove('_gid')
}

// // Send a custom event
// ReactGA.event({
//   category: "your category",
//   action: "your action",
//   label: "your label", // optional
//   value: 99, // optional, must be a number
//   nonInteraction: true, // optional, true/false
//   transport: "xhr", // optional, beacon/xhr/image
// });
